import React from "react";
import {
  AlertTriangle,
  ClearFilter,
  EmptyStateIllustration,
  TableSearchEmptyIllustration
} from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";

/** Already-translated copy for every view of the empty state. */
export interface DataTableEmptyStateLabels {
  title: string;
  filtersDescription: string;
  searchDescription: string;
  clearFilters: string;
  clearSearch: string;
  errorTitle: string;
  errorDescription: string;
  retry: string;
}

interface Props {
  view: "filters" | "search" | "error";
  labels: DataTableEmptyStateLabels;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  onRetry?: () => void;
}

const DataTableEmptyState: React.FC<Props> = ({
  view,
  labels,
  onClearFilters,
  onClearSearch,
  onRetry
}) => {
  if (view === "error") {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        data-test="table-error-state"
        role="alert"
      >
        <div className="flex flex-col items-center gap-6 w-[24rem] text-center">
          <IconContainer size="lg" color="error">
            <AlertTriangle />
          </IconContainer>
          <div className="flex flex-col gap-2">
            <Text variant="lgSubtitle" className="normal-case">
              {labels.errorTitle}
            </Text>
            <Text variant="lgBodySecondary">{labels.errorDescription}</Text>
          </div>
          {onRetry && (
            <Button
              size="lg"
              onClick={() => onRetry()}
              variant="secondary"
              className="w-fit"
            >
              {labels.retry}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="flex items-center justify-center gap-4">
        {view === "filters" ? (
          <div className="w-[280px] [&>svg]:w-full [&>svg]:h-auto">
            <EmptyStateIllustration />
          </div>
        ) : (
          <div className="w-[280px] [&>svg]:w-full [&>svg]:h-auto">
            <TableSearchEmptyIllustration />
          </div>
        )}
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-6">
            <Text variant="lgSubtitle" className="normal-case">
              {labels.title}
            </Text>
            <Text
              variant="lgBodySecondary"
              className={cn(view === "search" ? "w-[20rem]" : "w-[22rem]")}
            >
              {view === "search"
                ? labels.searchDescription
                : labels.filtersDescription}
            </Text>
          </div>
          {view === "filters" && onClearFilters && (
            <Button
              size="lg"
              onClick={onClearFilters}
              variant="secondary"
              className="w-fit flex"
            >
              <IconContainer size="5">
                <ClearFilter />
              </IconContainer>
              {labels.clearFilters}
            </Button>
          )}
          {view === "search" && onClearSearch && (
            <Button
              size="lg"
              onClick={onClearSearch}
              variant="secondary"
              className="w-fit flex"
            >
              {labels.clearSearch}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataTableEmptyState;
